import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatSydneyDateTime, formatSydneyTime } from '../../utils/sydneyDateTime'
import { asArray } from '../../utils/normalizeApiData'

function resolveAlertVenueName(alertRow, venues = []) {
  if (alertRow?.venue_name) return alertRow.venue_name
  const venueId = String(alertRow?.venue_id || '').trim().toLowerCase()
  if (venueId) {
    const match = venues.find((v) => v.id === venueId)
    if (match?.name) return match.name
  }
  const details = alertRow?.details && typeof alertRow.details === 'object' ? alertRow.details : {}
  const triggered = details.triggered_data || details.triggeredData || {}
  return (
    alertRow?.venue_code_raw
    || alertRow?.venue_code
    || details.venue
    || details.Venue
    || triggered.Venue
    || triggered.VenueCode
    || '—'
  )
}

function useVenues() {
  const [venues, setVenues] = useState([])
  useEffect(() => {
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'))
        const j = await r.json()
        if (j.success && asArray(j.data).length > 0) {
          setVenues(
            asArray(j.data).map((v) => ({
              id: String(v.venue_id ?? v.id).toLowerCase(),
              name: v.name,
              shortName: v.code || v.name,
            }))
          )
        }
      } catch {}
    })()
  }, [])
  return venues
}

const SEV_BADGE = {
  CRITICAL: 'bg-red-100 text-red-800',
  HIGH: 'bg-orange-100 text-orange-800',
  MEDIUM: 'bg-yellow-100 text-yellow-800',
  LOW: 'bg-green-100 text-green-800',
}

function alertWhenMs(a) {
  const t = a.last_updated || a.first_seen
  if (!t) return NaN
  const d = new Date(t)
  return d.getTime()
}

/** Higher = more urgent — used for sorting within groups and ordering rule sections */
function severityRank(a) {
  const s = String((a && a.severity) || 'MEDIUM').toUpperCase()
  if (s === 'CRITICAL') return 4
  if (s === 'HIGH') return 3
  if (s === 'LOW') return 1
  return 2
}

function severityGroupKey(a) {
  const r = severityRank(a)
  if (r === 4) return 'CRITICAL'
  if (r === 3) return 'HIGH'
  if (r === 1) return 'LOW'
  return 'MEDIUM'
}

function sortAlertsWithinGroup(items) {
  return [...items].sort((a, b) => {
    const rs = severityRank(b) - severityRank(a)
    if (rs !== 0) return rs
    const ta = alertWhenMs(a)
    const tb = alertWhenMs(b)
    if (Number.isFinite(tb) && Number.isFinite(ta)) return tb - ta
    return String(b.id || '').localeCompare(String(a.id || ''))
  })
}

function dateBucketKeyLocal(a) {
  const ms = alertWhenMs(a)
  if (!Number.isFinite(ms)) return 'unknown'
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Australia/Sydney',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(ms))
  } catch {
    return 'unknown'
  }
}

function sydneyYearNow() {
  return Number(new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', year: 'numeric' }).format(new Date()))
}

const ALERT_YEAR_OPTIONS = (() => {
  const current = sydneyYearNow()
  const years = []
  for (let y = current; y >= 2020; y -= 1) years.push(String(y))
  return years
})()

const ALERT_MONTH_OPTIONS = [
  ['1', 'January'],
  ['2', 'February'],
  ['3', 'March'],
  ['4', 'April'],
  ['5', 'May'],
  ['6', 'June'],
  ['7', 'July'],
  ['8', 'August'],
  ['9', 'September'],
  ['10', 'October'],
  ['11', 'November'],
  ['12', 'December'],
]

function formatDateHeading(isoKey) {
  if (isoKey === 'unknown') return 'Unknown date'
  const [y, m, d] = isoKey.split('-').map(Number)
  if (!y || !m || !d) return isoKey
  return new Date(y, m - 1, d).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function WhenAndSeverityCell({ alertRow, whenMode = 'full', omitSeverity = false }) {
  const sev = String(alertRow.severity || 'MEDIUM').toUpperCase()
  const dt = alertRow.last_updated || alertRow.first_seen
  const whenLabel =
    dt == null
      ? '—'
      : whenMode === 'time-only'
        ? formatSydneyTime(dt, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
        : formatSydneyDateTime(dt, { dateStyle: 'medium', timeStyle: 'short' })
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm text-gray-700">{whenLabel}</span>
      {!omitSeverity && (
        <span className={`inline-flex w-fit items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${SEV_BADGE[sev] || SEV_BADGE.MEDIUM}`}>
          {sev}
        </span>
      )}
    </div>
  )
}

function isCardedAlert(a) {
  return Boolean(a?.member_id && String(a.member_id).trim() !== '')
}

function passesCardedFilter(a, cardedFilter) {
  if (cardedFilter === 'all') return true
  const carded = isCardedAlert(a)
  return cardedFilter === 'carded' ? carded : !carded
}

function CardedMemberCell({ alertRow }) {
  const carded = isCardedAlert(alertRow)
  return (
    <div className="flex flex-col gap-1">
      <span
        className={`inline-flex w-fit items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${
          carded ? 'bg-sky-50 text-sky-800 border border-sky-200' : 'bg-gray-100 text-gray-600 border border-gray-200'
        }`}
      >
        {carded ? 'Carded' : 'Uncarded'}
      </span>
      <span className="text-sm font-mono text-gray-900">{carded ? alertRow.member_id : '—'}</span>
    </div>
  )
}

function AlertRow({ a, venues, onClick, whenMode = 'full', omitSeverity = false }) {
  const venueName = resolveAlertVenueName(a, venues)
  return (
    <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => onClick?.(a)}>
      <td className="px-4 py-2 align-top">
        <WhenAndSeverityCell alertRow={a} whenMode={whenMode} omitSeverity={omitSeverity} />
      </td>
      <td className="px-4 py-2 text-sm">{venueName}</td>
      <td className="px-4 py-2">
        <div className="text-sm font-medium text-gray-900">{a.rule_name} <span className="text-xs text-gray-400 ml-1">({a.rule_id})</span></div>
        <div className="text-xs text-gray-500">{a.category}</div>
      </td>
      <td className="px-4 py-2 text-sm capitalize">{a.rule_type}</td>
      <td className="px-4 py-2 align-top">
        <CardedMemberCell alertRow={a} />
      </td>
      <td className="px-4 py-2 text-sm font-mono">{a.session_id || '—'}</td>
      <td className="px-4 py-2 text-sm font-mono">{a.transaction_id || '—'}</td>
      <td className="px-4 py-2 text-sm">{a.egm_house_no || '—'}</td>
      <td className="px-4 py-2 text-sm capitalize">{a.status}</td>
    </tr>
  )
}

function AlertsSummaryCards({ loading, total, bySev, topRules }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Alerts by severity</h3>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((sev) => (
                <div
                  key={sev}
                  className={`rounded-lg border border-gray-200 px-3 py-3 ${SEV_BADGE[sev] || SEV_BADGE.MEDIUM}`}
                >
                  <div className="text-[10px] font-semibold uppercase tracking-wide opacity-90">{sev}</div>
                  <div className="text-2xl font-bold tabular-nums mt-1">{bySev[sev] ?? 0}</div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-500">
              On this page: <span className="font-semibold text-gray-700">{total}</span>
            </p>
          </>
        )}
      </div>
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Top rules by alert count</h3>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : topRules.length === 0 ? (
          <p className="text-sm text-gray-500">No alerts match current filters.</p>
        ) : (
          <ul className="space-y-2.5">
            {topRules.map((r, i) => (
              <li key={`${r.ruleKey}`} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-gray-800 min-w-0">
                  <span className="text-gray-400 font-medium mr-1.5">{i + 1}.</span>
                  <span className="font-medium">{r.rule_name}</span>
                  {r.rule_id != null && r.rule_id !== '' && (
                    <span className="text-xs text-gray-400 ml-1">({r.rule_id})</span>
                  )}
                </span>
                <span className="font-semibold text-gray-900 tabular-nums shrink-0">{r.count}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function ClusterRow({ cluster, venues, onSelectAlert, expanded, onToggle }) {
  const venueName = resolveAlertVenueName(cluster, venues)
  return (
    <>
      <tr className="bg-blue-50 hover:bg-blue-100 cursor-pointer" onClick={onToggle}>
        <td className="px-4 py-2 align-top">
          <WhenAndSeverityCell alertRow={cluster} whenMode="full" />
        </td>
        <td className="px-4 py-2 text-sm">{venueName}</td>
        <td className="px-4 py-2">
          <div className="text-sm font-medium text-gray-900">
            {expanded ? '▼' : '▶'} {cluster.rule_name}
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {cluster.alert_count} alert{cluster.alert_count !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="text-xs text-gray-500">{cluster.category}</div>
        </td>
        <td className="px-4 py-2 text-sm capitalize">{cluster.alerts?.[0]?.rule_type || '—'}</td>
        <td className="px-4 py-2 align-top">
          <CardedMemberCell alertRow={cluster} />
        </td>
        <td className="px-4 py-2 text-sm font-mono">—</td>
        <td className="px-4 py-2 text-sm font-mono">—</td>
        <td className="px-4 py-2 text-sm">—</td>
        <td className="px-4 py-2 text-sm capitalize">{cluster.status}</td>
      </tr>
      {expanded && cluster.alerts?.map(a => (
        <AlertRow key={a.id} a={a} venues={venues} onClick={onSelectAlert} whenMode="full" />
      ))}
    </>
  )
}

const FILTER_CONTROL = 'w-full h-8 px-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-psg-teal'

function FilterField({ label, className = '', children }) {
  return (
    <label className={`flex flex-col gap-0.5 min-w-0 ${className}`}>
      <span className="text-[11px] font-medium text-gray-500 leading-none">{label}</span>
      {children}
    </label>
  )
}

function Alerts() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const venues = useVenues()
  const [alerts, setAlerts] = useState([])
  const [clusteredData, setClusteredData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [q, setQ] = useState('')
  const [venue, setVenue] = useState('all')
  const [status, setStatus] = useState('all')
  const [severity, setSeverity] = useState('all')
  const [type, setType] = useState('all')
  const [cardedFilter, setCardedFilter] = useState('all') // 'all' | 'carded' | 'uncarded'
  const [groupBy, setGroupBy] = useState('date') // 'none' | 'date' | 'rule' | 'severity' | 'member' | 'egm' | 'cluster'
  const [year, setYear] = useState('')
  const [month, setMonth] = useState('')
  const [alertDate, setAlertDate] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [total, setTotal] = useState(0)
  const [expandedClusters, setExpandedClusters] = useState({})
  const [stagingCount, setStagingCount] = useState(0)

  const filterSignature = [q, status, severity, type, groupBy, year, month, alertDate, pageSize].join('|')
  const [appliedFilters, setAppliedFilters] = useState(filterSignature)
  if (appliedFilters !== filterSignature) {
    setAppliedFilters(filterSignature)
    if (page !== 1) setPage(1)
  }

  useEffect(() => {
    const fromUrl = searchParams.get('q') || searchParams.get('member_id')
    if (fromUrl) setQ(fromUrl)
    const venueFromUrl = searchParams.get('venue')
    if (venueFromUrl) setVenue(String(venueFromUrl).toLowerCase())
  }, [searchParams])

  const loadAlerts = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (q) params.set('member_id', q.slice(0, 64))
      if (status !== 'all') params.set('status', status)
      if (severity !== 'all') params.set('severity', severity)
      if (type !== 'all') params.set('type', type)
      if (groupBy === 'cluster') params.set('group_by_cluster', '1')
      if (alertDate) {
        params.set('date', alertDate)
      } else {
        if (year) params.set('year', year)
        if (month) params.set('month', String(Number(month)))
      }
      params.set('limit', String(pageSize))
      params.set('offset', String((Math.max(page, 1) - 1) * pageSize))
      const resp = await authFetch(getLocalApiUrl(`/api/alerts/list?${params.toString()}`))
      const json = await resp.json()
      const pageTotal = Number(json?.total)
      setTotal(Number.isFinite(pageTotal) ? pageTotal : asArray(json?.data).length)
      if (json.grouped) {
        setClusteredData({ clusters: asArray(json.data), unclustered: asArray(json.unclustered) })
        setAlerts([])
      } else {
        setAlerts(asArray(json?.data))
        setClusteredData(null)
      }
    } catch (e) {
      setAlerts([])
      setClusteredData(null)
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [q, status, severity, type, groupBy, year, month, alertDate, page, pageSize])

  useEffect(() => {
    loadAlerts()
  }, [loadAlerts])

  // Check staging queue depth for pending consolidation indicator
  useEffect(() => {
    let cancelled = false
    const checkStaging = async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/alerts/staging-count'))
        const j = await r.json()
        if (!cancelled && j.success) setStagingCount(j.count || 0)
      } catch { /* staging endpoint may not exist yet */ }
    }
    checkStaging()
    const timer = setInterval(checkStaging, 30000) // refresh every 30s
    return () => { cancelled = true; clearInterval(timer) }
  }, [])

  const rows = useMemo(() => {
    let out = alerts
    if (venue !== 'all') {
      const v = String(venue).toLowerCase()
      out = out.filter((a) => {
        const vid = String(a.venue_id || '').toLowerCase()
        return vid === v || vid.includes(v)
      })
    }
    if (cardedFilter !== 'all') {
      out = out.filter((a) => passesCardedFilter(a, cardedFilter))
    }
    return out
  }, [alerts, venue, cardedFilter])

  const filteredClusteredData = useMemo(() => {
    if (!clusteredData) return null
    let clusters = clusteredData.clusters
    let unclustered = clusteredData.unclustered
    if (venue !== 'all') {
      const v = String(venue).toLowerCase()
      clusters = clusters.filter((c) => {
        const vid = String(c.venue_id || '').toLowerCase()
        return vid === v || vid.includes(v)
      })
      unclustered = unclustered.filter((a) => {
        const vid = String(a.venue_id || '').toLowerCase()
        return vid === v || vid.includes(v)
      })
    }
    if (cardedFilter !== 'all') {
      clusters = clusters.filter((c) => passesCardedFilter(c, cardedFilter))
      unclustered = unclustered.filter((a) => passesCardedFilter(a, cardedFilter))
    }
    return { clusters, unclustered }
  }, [clusteredData, venue, cardedFilter])

  const summaryAlerts = useMemo(() => {
    if (groupBy === 'cluster' && filteredClusteredData) {
      const fromClusters = filteredClusteredData.clusters.flatMap((c) => c.alerts || [])
      return [...fromClusters, ...filteredClusteredData.unclustered]
    }
    return rows
  }, [groupBy, filteredClusteredData, rows])

  const summaryStats = useMemo(() => {
    const list = summaryAlerts
    const bySev = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 }
    const ruleMap = new Map()
    for (const a of list) {
      const sev = severityGroupKey(a)
      bySev[sev] = (bySev[sev] || 0) + 1
      const ruleKey = String(a.rule_id ?? a.rule_name ?? 'unknown')
      const prev = ruleMap.get(ruleKey)
      const count = (prev?.count ?? 0) + 1
      ruleMap.set(ruleKey, {
        ruleKey,
        rule_id: a.rule_id,
        rule_name: a.rule_name || ruleKey,
        count,
      })
    }
    const topRules = [...ruleMap.values()].sort((x, y) => y.count - x.count).slice(0, 5)
    return { total: list.length, bySev, topRules }
  }, [summaryAlerts])

  const grouped = useMemo(() => {
    if (groupBy === 'none' || groupBy === 'cluster') return null
    const map = {}
    for (const a of rows) {
      let key
      if (groupBy === 'date') {
        key = dateBucketKeyLocal(a)
      } else if (groupBy === 'rule') {
        key = String(a.rule_id || a.rule_name || 'unknown').trim() || 'unknown'
      } else if (groupBy === 'severity') {
        key = severityGroupKey(a)
      } else if (groupBy === 'member') {
        key = a.member_id || 'Unidentified'
      } else {
        key = a.egm_house_no || a.egm_name || 'Unknown EGM'
      }
      if (!map[key]) map[key] = []
      map[key].push(a)
    }
    const entries = Object.entries(map).map(([k, items]) => [k, sortAlertsWithinGroup(items)])

    if (groupBy === 'date') {
      entries.sort(([ka], [kb]) => {
        if (ka === 'unknown') return 1
        if (kb === 'unknown') return -1
        return kb.localeCompare(ka)
      })
    } else if (groupBy === 'rule') {
      entries.sort(([, itemsA], [, itemsB]) => {
        const maxA = Math.max(0, ...itemsA.map(severityRank))
        const maxB = Math.max(0, ...itemsB.map(severityRank))
        if (maxB !== maxA) return maxB - maxA
        if (itemsB.length !== itemsA.length) return itemsB.length - itemsA.length
        const nameA = itemsA[0]?.rule_name || ''
        const nameB = itemsB[0]?.rule_name || ''
        return String(nameA).localeCompare(String(nameB))
      })
    } else if (groupBy === 'severity') {
      entries.sort(([ka], [kb]) => severityRank({ severity: kb }) - severityRank({ severity: ka }))
    } else {
      entries.sort(([a], [b]) => String(a).localeCompare(String(b)))
    }
    return entries
  }, [rows, groupBy])

  const rowWhenMode = groupBy === 'date' || groupBy === 'severity' ? 'time-only' : 'full'
  const omitRowSeverity = groupBy === 'severity'
  const firstColHeader =
    groupBy === 'date' ? 'Time · severity' : groupBy === 'severity' ? 'Time' : 'When · severity'

  const toggleCluster = (clusterId) => {
    setExpandedClusters(prev => ({ ...prev, [clusterId]: !prev[clusterId] }))
  }

  return (
    <div className="space-y-4">
      <div className="card !p-3">
        <div className="flex flex-wrap items-end gap-2">
          <FilterField label="Member #" className="w-28">
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="M1001" className={FILTER_CONTROL} />
          </FilterField>
          <FilterField label="Venue" className="w-36">
            <select value={venue} onChange={e=>setVenue(e.target.value)} className={FILTER_CONTROL}>
              <option value="all">All</option>
              {venues.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Status" className="w-32">
            <select value={status} onChange={e=>setStatus(e.target.value)} className={FILTER_CONTROL}>
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="investigating">Investigating</option>
              <option value="suppressed">Suppressed</option>
              <option value="resolved">Resolved</option>
            </select>
          </FilterField>
          <FilterField label="Severity" className="w-28">
            <select value={severity} onChange={e=>setSeverity(e.target.value)} className={FILTER_CONTROL}>
              <option value="all">All</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </FilterField>
          <FilterField label="Type" className="w-32">
            <select value={type} onChange={e=>setType(e.target.value)} className={FILTER_CONTROL}>
              <option value="all">All</option>
              <option value="transaction">Transaction</option>
              <option value="session">Session</option>
              <option value="onboarding">Onboarding</option>
              <option value="ongoing">Ongoing</option>
            </select>
          </FilterField>
          <FilterField label="Carded" className="w-28">
            <select value={cardedFilter} onChange={(e) => setCardedFilter(e.target.value)} className={FILTER_CONTROL}>
              <option value="all">All</option>
              <option value="carded">Carded</option>
              <option value="uncarded">Uncarded</option>
            </select>
          </FilterField>
          <FilterField label="Group" className="w-28">
            <select value={groupBy} onChange={e=>setGroupBy(e.target.value)} className={FILTER_CONTROL}>
              <option value="none">None</option>
              <option value="date">Date</option>
              <option value="rule">Rule</option>
              <option value="severity">Severity</option>
              <option value="member">Member</option>
              <option value="egm">EGM</option>
              <option value="cluster">Cluster</option>
            </select>
          </FilterField>
          <FilterField label="Year" className="w-24">
            <select
              value={alertDate ? alertDate.slice(0, 4) : year}
              onChange={(e) => {
                setAlertDate('')
                setYear(e.target.value)
                if (!e.target.value) setMonth('')
              }}
              className={FILTER_CONTROL}
            >
              <option value="">All</option>
              {ALERT_YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Month" className="w-28">
            <select
              value={alertDate ? String(Number(alertDate.slice(5, 7))) : month}
              onChange={(e) => {
                const next = e.target.value
                setAlertDate('')
                setMonth(next)
                if (next && !year) setYear(String(sydneyYearNow()))
              }}
              className={FILTER_CONTROL}
            >
              <option value="">All</option>
              {ALERT_MONTH_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Date" className="w-36">
            <input
              type="date"
              value={alertDate}
              onChange={(e) => {
                const next = e.target.value
                setAlertDate(next)
                if (next) {
                  setYear(next.slice(0, 4))
                  setMonth(String(Number(next.slice(5, 7))))
                }
              }}
              className={FILTER_CONTROL}
            />
          </FilterField>
        </div>
      </div>

      <AlertsSummaryCards
        loading={loading}
        total={summaryStats.total}
        bySev={summaryStats.bySev}
        topRules={summaryStats.topRules}
      />

      {stagingCount > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          {stagingCount} trigger{stagingCount !== 1 ? 's' : ''} pending consolidation
          <button
            className="ml-auto text-xs text-amber-700 hover:text-amber-900 underline"
            onClick={async () => {
              try {
                await authFetch(getLocalApiUrl('/api/alerts/consolidate'), { method: 'POST' })
                setStagingCount(0)
                loadAlerts()
              } catch {}
            }}
          >
            Consolidate now
          </button>
        </div>
      )}

      <div className="card overflow-hidden">
        <AlertsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          onPage={setPage}
          onPageSize={setPageSize}
        />
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  {firstColHeader}
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Venue</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rule</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Carded · member</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Txn</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">EGM</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="9" className="px-4 py-6 text-center text-sm text-gray-500">Loading...</td></tr>
              ) : groupBy === 'cluster' && filteredClusteredData ? (
                <>
                  {filteredClusteredData.clusters.length === 0 && filteredClusteredData.unclustered.length === 0 ? (
                    <tr><td colSpan="9" className="px-4 py-6 text-center text-sm text-gray-500">No alerts</td></tr>
                  ) : (
                    <>
                      {filteredClusteredData.clusters.map(cluster => (
                        <ClusterRow
                          key={cluster.cluster_id}
                          cluster={cluster}
                          venues={venues}
                          onSelectAlert={(a) => navigate(`/monitor-safe/alerts/${a.id}`)}
                          expanded={!!expandedClusters[cluster.cluster_id]}
                          onToggle={() => toggleCluster(cluster.cluster_id)}
                        />
                      ))}
                      {filteredClusteredData.unclustered.length > 0 && (
                        <>
                          <tr className="bg-gray-100">
                            <td colSpan="9" className="px-4 py-2 text-sm font-semibold text-gray-600">
                              Unclustered <span className="text-xs font-normal text-gray-500">({filteredClusteredData.unclustered.length} alert{filteredClusteredData.unclustered.length !== 1 ? 's' : ''})</span>
                            </td>
                          </tr>
                          {filteredClusteredData.unclustered.map(a => (
                            <AlertRow key={a.id} a={a} venues={venues} onClick={(a) => navigate(`/monitor-safe/alerts/${a.id}`)} whenMode="full" />
                          ))}
                        </>
                      )}
                    </>
                  )}
                </>
              ) : rows.length === 0 ? (
                <tr><td colSpan="9" className="px-4 py-6 text-center text-sm text-gray-500">No alerts</td></tr>
              ) : grouped ? (
                grouped.map(([key, items]) => {
                  const first = items[0]
                  const maxSev = Math.max(0, ...items.map(severityRank))
                  const maxSevLabel = ['—', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'][maxSev] || 'MEDIUM'
                  let heading = ''
                  if (groupBy === 'date') heading = formatDateHeading(key)
                  else if (groupBy === 'rule') {
                    heading = `${first?.rule_name || 'Rule'} (${first?.rule_id ?? key})`
                  } else if (groupBy === 'severity') {
                    heading = key
                  } else if (groupBy === 'member') heading = `Member: ${key}`
                  else heading = `EGM: ${key}`

                  return (
                  <React.Fragment key={key}>
                    <tr className="bg-slate-100">
                      <td colSpan="9" className="px-4 py-2 text-sm font-semibold text-slate-700">
                        {groupBy === 'severity' ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase ${SEV_BADGE[key] || SEV_BADGE.MEDIUM}`}>
                            {heading}
                          </span>
                        ) : (
                          heading
                        )}{' '}
                        <span className="text-xs font-normal text-slate-500">
                          · {items.length} alert{items.length !== 1 ? 's' : ''}
                          {(groupBy === 'rule' || groupBy === 'member' || groupBy === 'egm') && (
                            <>
                              {' '}
                              · max severity{' '}
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${SEV_BADGE[maxSevLabel] || SEV_BADGE.MEDIUM}`}>
                                {maxSevLabel}
                              </span>
                            </>
                          )}
                        </span>
                      </td>
                    </tr>
                    {items.map(a => (
                      <AlertRow key={a.id} a={a} venues={venues} onClick={(a) => navigate(`/monitor-safe/alerts/${a.id}`)} whenMode={rowWhenMode} omitSeverity={omitRowSeverity} />
                    ))}
                  </React.Fragment>
                  )
                })
              ) : rows.map(a => (
                <AlertRow key={a.id} a={a} venues={venues} onClick={(a) => navigate(`/monitor-safe/alerts/${a.id}`)} whenMode="full" />
              ))}
            </tbody>
          </table>
        </div>
        <AlertsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          onPage={setPage}
          onPageSize={setPageSize}
        />
      </div>

    </div>
  )
}

function AlertsPager({ page, pageSize, total, loading, onPage, onPageSize }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize))
  const safePage = Math.min(Math.max(page, 1), totalPages)
  const rangeFrom = total === 0 ? 0 : (safePage - 1) * pageSize + 1
  const rangeTo = Math.min(safePage * pageSize, total)
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100">
      <div className="text-sm text-gray-600">
        {total === 0
          ? 'No alerts match the current filters'
          : `Showing ${rangeFrom.toLocaleString()}–${rangeTo.toLocaleString()} of ${total.toLocaleString()}`}
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">Page size</label>
        <select
          className="select-field text-sm py-1"
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value) || 100)}
        >
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <button
          type="button"
          className="btn-secondary text-sm py-1 px-3"
          disabled={loading || safePage <= 1}
          onClick={() => onPage(safePage - 1)}
        >
          Previous
        </button>
        <span className="text-sm text-gray-700 whitespace-nowrap">
          Page {total === 0 ? 0 : safePage} of {total === 0 ? 0 : totalPages}
        </span>
        <button
          type="button"
          className="btn-secondary text-sm py-1 px-3"
          disabled={loading || total === 0 || safePage >= totalPages}
          onClick={() => onPage(safePage + 1)}
        >
          Next
        </button>
      </div>
    </div>
  )
}

export default Alerts
